/**
 * Screen registry: every UX-01 screen with its route. The router and the side nav are both generated from this
 * list, so a screen is added in exactly one place.
 *
 * W1 owns this file. Stream W2: to wire a screen, send a one-line change setting `Component` on your entry.
 */
import { SprintRetrospectivePage } from "../features/retrospective/SprintRetrospectivePage";
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

export type ScreenTag = "MVP-critical" | "MVP-reduced" | "Flagged" | "Post-go-live";
export type NavGroupName = "Home" | "Plan" | "Operate" | "Register" | "Settings";
export type Stream = "W1" | "W2";

export interface ScreenDef {
  /** UX-01 screen id, e.g. "S4". One screen may own several routes (S3, S8). */
  id: string;
  title: string;
  /** React Router pattern. Project screens live under /p/:projectKey. */
  path: string;
  tag: ScreenTag;
  stream: Stream;
  /**
   * `guide`: the nav link's `data-guide`, for guided tours that point at it (features/guides). `key`: the entry's stable
   * key in Settings → Menu (contract `Me.menu`, the server's `MenuCatalog`; `MenuCatalogueTests` keeps the two lists equal).
   * `order`: the default place within the group, used until the menu says otherwise.
   */
  nav?: { group: NavGroupName; key: string; label: string; order: number; guide?: string };
  /** Route component. Absent: a titled placeholder. */
  Component?: LazyExoticComponent<ComponentType> | ComponentType;
  /** Feature flag (no Features__ prefix) that hides the nav entry when off (UX-01 4.12). */
  navFlag?: string;
  /**
   * Batch 18 (stream S0): feature flag (no Features__ prefix) the ROUTE itself needs. Off: the route is "Page not found",
   * as if it did not exist (the flagged screens of batch 18 ship dark). Independent of `navFlag`, which only hides the entry.
   */
  routeFlag?: string;
  /** Partners never see this entry or screen (UX-01 4.8). */
  hideForPartner?: boolean;
  /**
   * Wiki W1 (WIKI-SPEC-20261025a D1): partners never see the nav ENTRY, and the route answers them with the normal "Page not
   * found" (the page itself decides), never the "not available to partner staff" page `hideForPartner` shows.
   */
  hideNavForPartner?: boolean;
  /**
   * Settings ruling (2 Oct 2026): a SystemAdmin only, and never in test mode (`Session.canOpenSettings`). Every Settings
   * entry and its sub-routes carry it; the server refuses the endpoints behind them the same way.
   */
  requiresSystemAdmin?: boolean;
  /**
   * Settings → Permissions (design §3): a SystemAdmin in test mode still opens the page, read-only, so the matrix stays on
   * screen while they check a change as a role. Only with `requiresSystemAdmin`; the nav still hides it in test mode.
   */
  readOnlyInTestMode?: boolean;
  /**
   * O-R12-1: the side nav hides an entry whose page would only refuse this caller. `capabilities`: permission codes the caller
   * must hold on the nav's project, ALL of them (batch 20 B20-1: an entry's `menu.*` code ANDed with any read gate it had, e.g.
   * Events `event.view` + `menu.events`; the CM report: `cm.view`). The cross-project entries ask "any project":
   * `devopsQueue`: `mayOpenDevOpsQueue`; `emergencyReport` (batch 17): a Chief Pilot of some project or a System Admin, outside
   * test mode (`mayOpenEmergencyReport`); `approvals` (batch 20): `deployment.approve_pm` or `deployment.approve_director` on some
   * project (`mayOpenApprovals`). Never a role name here. The page keeps its own refusal for a direct visit; the server still
   * decides, and never checks a `menu.*` code.
   */
  navGate?: { capabilities: readonly string[] } | "devopsQueue" | "emergencyReport" | "approvals" | "statusReports";
  note?: string;
}

const routeModuleLoaders: (() => Promise<unknown>)[] = [];

function named<K extends string>(loader: () => Promise<Record<K, ComponentType>>, name: K) {
  routeModuleLoaders.push(loader);
  return lazy(async () => ({ default: (await loader())[name] }));
}

/**
 * Imports every lazily loaded route module up front. Not used by the app (routes stay code-split); the test harness
 * (src/test/renderApp.tsx) awaits it once per test file so a first render times the page's own fetch and render,
 * not a cold module import racing a findBy timeout on a loaded machine.
 */
export async function preloadScreens(): Promise<void> {
  await Promise.all(routeModuleLoaders.map((load) => load()));
}

const HomePage = named(() => import("../features/items/HomePage"), "HomePage");
const ProjectItemsPage = named(() => import("../features/items/ProjectItemsPage"), "ProjectItemsPage");
const ChangeManagementPage = named(() => import("../features/items/ChangeManagementPage"), "ChangeManagementPage");
const IssueActionPage = named(() => import("../features/items/IssueActionPage"), "IssueActionPage");
const ItemDetailPage = named(() => import("../features/items/ItemDetailPage"), "ItemDetailPage");
const CreateItemPage = named(() => import("../features/items/CreateItemPage"), "CreateItemPage");
const EditItemPage = named(() => import("../features/items/EditItemPage"), "EditItemPage");
const TimeRedirect = named(() => import("../features/items/TimeRedirect"), "TimeRedirect");
const SearchPage = named(() => import("../features/search/SearchPage"), "SearchPage");
const BacklogPage = named(() => import("../features/boards/BacklogPage"), "BacklogPage");
const BoardPage = named(() => import("../features/boards/BoardPage"), "BoardPage");
const SprintBoardPage = named(() => import("../features/boards/SprintBoardPage"), "SprintBoardPage");
const KanbanPage = named(() => import("../features/boards/KanbanPage"), "KanbanPage");
const OlderItemsPage = named(() => import("../features/boards/OlderItemsPage"), "OlderItemsPage");
const ServiceDeskPage = named(() => import("../features/servicedesk/ServiceDeskPage"), "ServiceDeskPage");
const CmReportPage = named(() => import("../features/servicedesk/CmReportPage"), "CmReportPage");
const EventsListPage = named(() => import("../features/events/EventsListPage"), "EventsListPage");
const EventPage = named(() => import("../features/events/EventPage"), "EventPage");
const DeploymentsListPage = named(() => import("../features/deployments/DeploymentsListPage"), "DeploymentsListPage");
const DeploymentBacklogPage = named(() => import("../features/deployments/DeploymentBacklogPage"), "DeploymentBacklogPage");
const DevOpsQueuePage = named(() => import("../features/devops/DevOpsQueuePage"), "DevOpsQueuePage");
const SlaAdminPage = named(() => import("../features/settings/SlaAdminPage"), "SlaAdminPage");
const MenuSettingsPage = named(() => import("../features/settings/menu/MenuSettingsPage"), "MenuSettingsPage");
const SecurityPage = named(() => import("../features/settings/security/SecurityPage"), "SecurityPage");
const AccessTokensPage = named(() => import("../features/account/AccessTokensPage"), "AccessTokensPage");
const PermissionsPage = named(() => import("../features/settings/permissions/PermissionsPage"), "PermissionsPage");
const IntegrationKeysPage = named(() => import("../features/settings/integrations/IntegrationKeysPage"), "IntegrationKeysPage");
const ConnectedToolsPage = named(() => import("../features/settings/connectedTools/ConnectedToolsPage"), "ConnectedToolsPage");
const ApprovalsPage = named(() => import("../features/approvals/ApprovalsPage"), "ApprovalsPage");
const UsersPage = named(() => import("../features/admin/UsersPage"), "UsersPage");
const ProjectsAdminPage = named(() => import("../features/admin/ProjectsAdminPage"), "ProjectsAdminPage");
const ProjectMembersPage = named(() => import("../features/admin/ProjectsAdminPage"), "ProjectMembersPage");
const MentionsRoute = named(() => import("../features/mentions/MentionsRoute"), "MentionsRoute");
const NotificationsPage = named(() => import("../features/notifications/NotificationsPage"), "NotificationsPage");
const NotificationSettingsPage = named(() => import("../features/notifications/NotificationSettingsPage"), "NotificationSettingsPage");
const EmergencyCodePage = named(() => import("../features/emergency/EmergencyCodePage"), "EmergencyCodePage");
const EmergencyReportPage = named(() => import("../features/emergency/EmergencyReportPage"), "EmergencyReportPage");
const ReportsPage = named(() => import("../features/reports/ReportsPage"), "ReportsPage");
const ProjectStatusPage = named(() => import("../features/status/ProjectStatusPage"), "ProjectStatusPage");
const StatusReportsPage = named(() => import("../features/status/StatusReportsPage"), "StatusReportsPage");
const PresentationsPage = named(() => import("../features/presentations/PresentationsPage"), "PresentationsPage");
const WikiHome = named(() => import("../features/wiki/WikiHome"), "WikiHome");
const WikiPageScreen = named(() => import("../features/wiki/WikiPageView"), "WikiPageScreen");
const WikiEditorPage = named(() => import("../features/wiki/WikiEditorPage"), "WikiEditorPage");
const WikiSettingsPage = named(() => import("../features/wiki/WikiSettingsPage"), "WikiSettingsPage");
const CompanyWikiMembers = named(() => import("../features/wiki/CompanyWikiMembers"), "CompanyWikiMembers");
const WikiImportScreen = named(() => import("../features/wiki/import/ImportScreen"), "ImportScreen");
const WikiMyImports = named(() => import("../features/wiki/import/MyImports"), "MyImports");
const ProfilePage = named(() => import("../features/profile/ProfilePage"), "ProfilePage");
const WhatsNewPage = named(() => import("../features/whatsNew/WhatsNewPage"), "WhatsNewPage");
const WorkflowsPage = named(() => import("../features/settings/workflows/WorkflowsPage"), "WorkflowsPage");
const WorkflowViewerPage = named(() => import("../features/settings/workflows/WorkflowViewerPage"), "WorkflowViewerPage");
const WorkflowEditorPage = named(() => import("../features/settings/workflows/WorkflowEditorPage"), "WorkflowEditorPage");

const POST_GO_LIVE = "A hard cut for 1 Oct (SA-01 §9.6). The spec is kept in UX-01 for after go-live.";

export const screens: readonly ScreenDef[] = [
  { id: "S1", title: "Home", path: "/", tag: "MVP-reduced", stream: "W1", nav: { group: "Home", key: "home", label: "Home", order: 1 }, Component: HomePage },
  {
    id: "S1",
    title: "Profile",
    path: "/me/profile",
    tag: "MVP-reduced",
    stream: "W1",
    // Ruling 2026-10-04 (Revision 94): every signed-in person, partners included; reached from the account menu, not the nav.
    Component: ProfilePage,
    note: "My profile: display name and picture; the real name is read-only. Account menu → Profile.",
  },
  {
    id: "S1",
    title: "Mentions",
    path: "/mentions",
    tag: "Flagged",
    stream: "W1",
    navFlag: "Mentions",
    // SA-04 §6: the caller's unread mentions and the email opt-out; partners get it too (D3), so no hideForPartner.
    nav: { group: "Home", key: "mentions", label: "Mentions", order: 2 },
    Component: MentionsRoute,
    note: "SA-04 §6 read side and §7.11 opt-out. Reached from Home's Needs attention line, the header (partners) and the nav. Batch 17: with Features__NotificationPanel on it redirects to /notifications (Mentions only).",
  },
  {
    id: "S1",
    title: "What's new",
    path: "/whats-new",
    tag: "MVP-reduced",
    stream: "W1",
    // Ruling of 17 Oct 2026: every release's notes, newest first, for internal staff only (partners: no entry, the page is not
    // available; an account whose every project role is Partner is treated the same in AppShell and WhatsNewPage). Also in the
    // account menu. No menu.* code: like Mentions it is not project-scoped, and Settings → Menu can still hide or move it.
    hideForPartner: true,
    nav: { group: "Home", key: "whats-new", label: "What's new", order: 3 },
    Component: WhatsNewPage,
    note: "What's new: every published release (go-live date, headline, bullets, GRF items closed) in Thai for everyone (ruling of 18 Oct 2026). Opening it records the newest release as seen; the release dialog opens by itself once per new release.",
  },
  // Wiki W1 (WIKI-SPEC-20261025a §13; Features__Wiki): the Company wiki. Every internal person reads it (D3: its editors are named by a
  // System Admin). Partners and external testers never see the entry, and the routes answer them "Page not found" (D1).
  {
    id: "S-W1",
    title: "Company wiki",
    path: "/wiki",
    tag: "Flagged",
    stream: "W1",
    navFlag: "Wiki",
    routeFlag: "Wiki",
    hideNavForPartner: true,
    nav: { group: "Home", key: "company-wiki", label: "Company wiki", order: 4 },
    Component: WikiHome,
    note: "Wiki W1 S-W1 Company wiki home: page tree, home page or 'No pages yet'.",
  },
  { id: "S-W9", title: "Company wiki settings", path: "/wiki/settings", tag: "Flagged", stream: "W1", routeFlag: "Wiki", Component: WikiSettingsPage },
  // Wiki W2 (WIKI-SPEC-20261026a §11A, §13 S-W16): the Markdown import's preview and report, and "My imports".
  { id: "S-W16", title: "My wiki imports", path: "/wiki/imports", tag: "Flagged", stream: "W2", routeFlag: "Wiki", Component: WikiMyImports },
  { id: "S-W16", title: "Wiki import", path: "/wiki/imports/:importId", tag: "Flagged", stream: "W2", routeFlag: "Wiki", Component: WikiImportScreen },
  { id: "S-W3", title: "Edit wiki page", path: "/wiki/:pageId/edit", tag: "Flagged", stream: "W1", routeFlag: "Wiki", Component: WikiEditorPage },
  { id: "S-W2", title: "Wiki page", path: "/wiki/:pageId/:slug?", tag: "Flagged", stream: "W1", routeFlag: "Wiki", Component: WikiPageScreen },
  {
    id: "S1",
    title: "Notifications",
    path: "/notifications",
    tag: "Flagged",
    stream: "W1",
    // Batch 17 (notifications panel design §7): reached from the bell's panel ("Open full page", ⋮ → Notification settings)
    // and from /mentions; not a nav entry. The page itself is not found with Features__NotificationPanel off.
    Component: NotificationsPage,
    note: "Batch 17 notifications full page: Direct / Watching tabs, Only show unread, ?tab= and the Mentions only filter, #settings. Behind Features__NotificationPanel.",
  },
  {
    id: "S1",
    title: "Notification settings",
    path: "/me/notifications/settings",
    tag: "Flagged",
    stream: "W1",
    // Batch 18 GRF-55 (B18-UX-SPECS §10; stream S3 sets Component). Not a nav entry: reached from the bell panel's gear, the
    // Notifications page, Profile and the account menu. Partners too (only the e-mail row).
    routeFlag: "NotificationSettings",
    Component: NotificationSettingsPage,
    note: "Batch 18 GRF-55 notification settings: Telegram link, the events × Bell / Telegram / E-mail grid (incl. Activity on items I watch), quiet hours, one Save. Behind Features__NotificationSettings; stream S3.",
  },
  {
    id: "S1",
    title: "Emergency code",
    path: "/emergency-codes/:requestId",
    tag: "Flagged",
    stream: "W1",
    // Batch 17 (Emergency OTP, design §6.5): the approver's code view, reached from the bell, Needs attention or Telegram. Not
    // a nav entry. Behind Features__EmergencyOtp; never for partners.
    hideForPartner: true,
    Component: EmergencyCodePage,
    note: "Batch 17 Emergency OTP approver view: card, requester, reason, countdown, Show code (never cached) with Copy, and the outcome once used, expired or locked.",
  },
  { id: "S1", title: "Items", path: "/p/:projectKey", tag: "MVP-critical", stream: "W1", nav: { group: "Plan", key: "items", label: "Items", order: 0, guide: "nav-items" }, Component: ProjectItemsPage },
  {
    id: "S1",
    title: "Change management",
    path: "/p/:projectKey/change-management",
    tag: "MVP-reduced",
    stream: "W1",
    // M-CR-Parent (2026-09-20): CR items never appear as their own board/backlog card, so this is the only list
    // view of them — "new menu to manage change request" from the original product ask.
    navGate: { capabilities: ["menu.change_management"] },
    nav: { group: "Plan", key: "change-management", label: "Change management", order: 1 },
    Component: ChangeManagementPage,
  },
  {
    id: "S1",
    title: "Issues and actions",
    path: "/p/:projectKey/issues-actions",
    tag: "MVP-reduced",
    stream: "W1",
    // M-Issue-Action-Split (2026-09-19): "implement issue and action as one separate menu" from the original
    // product ask, the sibling of Change management above (same CR-as-parent-era brief). RAID types (offBoard) have
    // no board/backlog presence of their own, so this dedicated list is their only menu, same reasoning as CR's.
    navGate: { capabilities: ["menu.issues_actions"] },
    nav: { group: "Plan", key: "issues-actions", label: "Issues and actions", order: 2 },
    Component: IssueActionPage,
  },
  { id: "S2", title: "Work item detail", path: "/items/:ref", tag: "MVP-critical", stream: "W1", Component: ItemDetailPage },
  { id: "S3", title: "Create work item", path: "/items/new", tag: "MVP-critical", stream: "W1", Component: CreateItemPage },
  { id: "S3", title: "Edit work item", path: "/items/:ref/edit", tag: "MVP-critical", stream: "W1", Component: EditItemPage },
  {
    id: "S4",
    title: "Sprint backlog",
    path: "/p/:projectKey/backlog",
    tag: "Flagged",
    stream: "W1",
    navFlag: "Sprints",
    navGate: { capabilities: ["menu.backlog"] },
    nav: { group: "Plan", key: "backlog", label: "Backlog", order: 3, guide: "nav-backlog" },
    Component: BacklogPage,
  },
  {
    id: "S5+S6",
    title: "Board",
    path: "/p/:projectKey/board",
    tag: "Flagged",
    stream: "W1",
    // M-Sprint-Kanban-Unification: no navFlag — a maintenance-mode project's board must stay reachable even when
    // Features__Sprints is off (the "Kanban" entry never carried one either); BoardPage picks sprint vs. maintenance
    // rendering itself from project.sprintsEnabled and the flag together.
    navGate: { capabilities: ["menu.board"] },
    nav: { group: "Plan", key: "board", label: "Board", order: 4, guide: "nav-board" },
    Component: BoardPage,
  },
  {
    id: "S25",
    title: "Reports",
    path: "/p/:projectKey/reports",
    tag: "Flagged",
    stream: "W1",
    // Batch 18 GRF-49 (B18-UX-SPECS §8; stream S7 sets Component). Partners get it too, built from partner-visible items only
    // (ruling 6), so no hideForPartner; the RAID section and the Logged column are left out for them in the page.
    navFlag: "ProjectReports",
    routeFlag: "ProjectReports",
    navGate: { capabilities: ["menu.reports"] },
    nav: { group: "Plan", key: "reports", label: "Reports", order: 5 },
    Component: ReportsPage,
    note: "Batch 18 GRF-49 project Reports: sprint burndown (items or story points), sprint scope, items by status and type, created vs resolved, workload, RAID (internal), SLA, CSV per section. Behind Features__ProjectReports; stream S7.",
  },
  {
    id: "S29",
    title: "Project status",
    path: "/p/:projectKey/status",
    tag: "Flagged",
    stream: "W1",
    // Batch 26 R1 (stream G2; rulings of 20 Oct 2026): the weekly report of the project's Capt.s and Chief Pilot (and a System
    // Admin). Entry and route behind Features__ProjectStatus; the entry only for status.view + menu.project_status holders, which
    // a partner or any other role never holds (locked rows L22). The page itself refuses anyone else.
    // Not hideForPartner on purpose (as Events): a partner gets "Page not found" from the page itself, never told it exists.
    navFlag: "ProjectStatus",
    routeFlag: "ProjectStatus",
    navGate: { capabilities: ["status.view", "menu.project_status"] },
    nav: { group: "Plan", key: "project-status", label: "Project status", order: 7 },
    Component: ProjectStatusPage,
    note: "Batch 26 R1 weekly project status: RAG (proposed), headline, phase, progress, progress table, rows, schedule bars, needs attention, deck, figures preview, Mark as reported / No change this week, earlier weeks read-only. Behind Features__ProjectStatus.",
  },
  {
    id: "S29",
    title: "Status reports",
    path: "/status-reports",
    tag: "Flagged",
    stream: "W1",
    // Batch 26 R1 (B26-PLAN-DESIGN §10 answer 1): every reporting project's state this week. The Chief Pilot sees all of theirs;
    // a Capt. their own. Shown to anyone who may read a project's status somewhere (`mayOpenStatusReports`).
    navFlag: "ProjectStatus",
    routeFlag: "ProjectStatus",
    navGate: "statusReports",
    nav: { group: "Plan", key: "status-reports", label: "Status reports", order: 8 },
    Component: StatusReportsPage,
    note: "Batch 26 R1 Status reports: each reporting project's state for the week (reported, no change, late, not reported), who marked it, the Capt.s. Behind Features__ProjectStatus.",
  },
  // Wiki W1 (WIKI-SPEC-20261025a §13; Features__Wiki): the project's wiki, for wiki.view + menu.wiki holders (never a partner: every
  // wiki cell of the Partner column is locked off, L-WIKI-INTERNAL). The page itself answers a partner "Page not found" (D1).
  {
    id: "S-W1",
    title: "Wiki",
    path: "/p/:projectKey/wiki",
    tag: "Flagged",
    stream: "W1",
    navFlag: "Wiki",
    routeFlag: "Wiki",
    navGate: { capabilities: ["wiki.view", "menu.wiki"] },
    nav: { group: "Plan", key: "wiki", label: "Wiki", order: 9 },
    Component: WikiHome,
    note: "Wiki W1 S-W1 project wiki home: page tree (drag to move on a desktop, Move… on a phone), home page or 'No pages yet'.",
  },
  // Presentations (PRESENT-SPEC-20261028 §7.1, §8; Features__Presentations): members of the project only (present.view +
  // menu.presentations; never a partner or the First Officer). The controller and /present are chromeless routes (routes.tsx).
  {
    id: "S-PR1",
    title: "Presentations",
    path: "/p/:projectKey/presentations",
    tag: "Flagged",
    stream: "W2",
    navFlag: "Presentations",
    routeFlag: "Presentations",
    hideNavForPartner: true,
    navGate: { capabilities: ["present.view", "menu.presentations"] },
    nav: { group: "Plan", key: "presentations", label: "Presentations", order: 10 },
    Component: PresentationsPage,
    note: "Presentations: upload an HTML package in parts, versions and roll back, past sessions, Present.",
  },
  { id: "S-W9", title: "Wiki settings", path: "/p/:projectKey/wiki/settings", tag: "Flagged", stream: "W1", routeFlag: "Wiki", Component: WikiSettingsPage },
  { id: "S-W16", title: "Wiki import", path: "/p/:projectKey/wiki/imports/:importId", tag: "Flagged", stream: "W2", routeFlag: "Wiki", Component: WikiImportScreen },
  { id: "S-W3", title: "Edit wiki page", path: "/p/:projectKey/wiki/:pageId/edit", tag: "Flagged", stream: "W1", routeFlag: "Wiki", Component: WikiEditorPage },
  { id: "S-W2", title: "Wiki page", path: "/p/:projectKey/wiki/:pageId/:slug?", tag: "Flagged", stream: "W1", routeFlag: "Wiki", Component: WikiPageScreen },
  {
    id: "S5",
    title: "Active sprint",
    path: "/p/:projectKey/sprint",
    tag: "Flagged",
    stream: "W1",
    // Not a nav entry (superseded by the unified "Board" entry above): kept as a direct route only, for existing
    // links and the fixed sprint-scoped view regardless of a project's own board-mode setting.
    Component: SprintBoardPage,
  },
  {
    id: "S6",
    title: "Kanban board",
    path: "/p/:projectKey/kanban",
    tag: "Flagged",
    stream: "W1",
    // Not a nav entry (superseded by the unified "Board" entry above): BacklogPage's "Kanban board" fallback link
    // and ScreenRoute's Features__Sprints-off redirect both still target this fixed, always-unscoped view.
    Component: KanbanPage,
  },
  {
    id: "S12",
    title: "Older work items",
    path: "/p/:projectKey/kanban/older",
    tag: "Flagged",
    stream: "W1",
    Component: OlderItemsPage,
    note: '"See older work items" (SA-01 §4.5a item 8.8.1, UX-01 S6-AC7): a fixed, read-only work item list, not a nav entry.',
  },
  {
    id: "S26",
    title: "Sprint retrospective",
    path: "/p/:projectKey/sprints/:sprintId/retrospective",
    tag: "Flagged",
    stream: "W1",
    // Batch 18 GRF-50 (B18-UX-SPECS §9; stream S6 sets Component). Not a nav entry: reached from Complete sprint, Reports and
    // the backlog header. Internal only (ruling R5 A), so partners get "not available".
    Component: SprintRetrospectivePage,
    hideForPartner: true,
    routeFlag: "SprintRetrospective",
    note: "Batch 18 GRF-50 sprint retrospective: facts strip, Went well / To improve / Actions, action entries made into linked ACTION items, Copy as text. Behind Features__SprintRetrospective; stream S6.",
  },
  {
    id: "S7",
    title: "Service desk",
    path: "/p/:projectKey/service-desk",
    tag: "MVP-critical",
    stream: "W2",
    navGate: { capabilities: ["menu.service_desk"] },
    nav: { group: "Operate", key: "service-desk", label: "Service desk", order: 1 },
    Component: ServiceDeskPage,
  },
  {
    id: "S27",
    title: "Events",
    path: "/p/:projectKey/events",
    tag: "Flagged",
    stream: "W1",
    // Batch 19 F3 (UX-B19-EVENTS §2.1; stream S7c): Operate, right after Service desk. Internal only: behind Features__Events (route
    // and entry); the entry only for holders of event.view, which a partner never holds (locked). Not hideForPartner on purpose:
    // EV-AC1 wants "Page not found" for a partner, not "not available to partner staff", so the page itself answers that.
    navFlag: "Events",
    routeFlag: "Events",
    navGate: { capabilities: ["event.view", "menu.events"] },
    nav: { group: "Operate", key: "events", label: "Events", order: 1.2 },
    Component: EventsListPage,
    note: "Batch 19 F3 Events admin: a project's events (rounds of work with dates, e.g. UAT), New event for event.manage holders. Behind Features__Events; stream S7c.",
  },
  {
    id: "S27",
    title: "Event",
    path: "/p/:projectKey/events/:eventId",
    tag: "Flagged",
    stream: "W1",
    routeFlag: "Events",
    Component: EventPage,
    note: "Batch 19 F3 one event: Overview (counts by status group and severity) and Lists (dropdown lists and values; copy from another event). Testers tab and Excel import arrive with later streams.",
  },
  {
    id: "S7",
    title: "CM report",
    path: "/p/:projectKey/service-desk/cm-report",
    tag: "MVP-reduced",
    stream: "W2",
    hideForPartner: true,
    navGate: { capabilities: ["cm.view"] },
    nav: { group: "Operate", key: "cm-report", label: "CM report", order: 1.5 },
    Component: CmReportPage,
    note: "M-CM-Report (2026-09-19): tick which SERVICE_REQUEST/INCIDENT tickets belong in this month's BMTA MA report and export the CM section as xlsx. Role-gated in-page to ProjectManager/ProjectDirector/SystemAdmin (sd.report), same as the SLA MA report.",
  },
  {
    id: "S8",
    title: "Deployments",
    path: "/p/:projectKey/deployments",
    tag: "MVP-critical",
    stream: "W2",
    navGate: { capabilities: ["menu.deployments"] },
    nav: { group: "Operate", key: "deployments", label: "Deployments", order: 2 },
    Component: DeploymentsListPage,
  },
  {
    id: "S8",
    title: "Deployment backlog",
    path: "/p/:projectKey/deployment-backlog",
    tag: "MVP-critical",
    stream: "W2",
    hideForPartner: true,
    navGate: { capabilities: ["menu.deployment_backlog"] },
    nav: { group: "Operate", key: "deployment-backlog", label: "Deployment backlog", order: 2.1 },
    Component: DeploymentBacklogPage,
    note: "SA-05 §4.2 (R4): a derived list, no new status. Cards in their ready status and not booked in an open deployment of the environment; bulk add to a deployment; hidden from partners (§7).",
  },
  {
    id: "S8",
    title: "Approvals",
    path: "/me/approvals",
    tag: "Flagged",
    stream: "W2",
    navFlag: "ApprovalsQueueUi",
    hideForPartner: true,
    navGate: "approvals",
    nav: { group: "Operate", key: "approvals", label: "Approvals", order: 3 },
    Component: ApprovalsPage,
  },
  {
    id: "S17",
    title: "DevOps queue",
    path: "/devops",
    tag: "Flagged",
    stream: "W2",
    navFlag: "DevOpsQueueUi",
    hideForPartner: true,
    navGate: "devopsQueue",
    nav: { group: "Operate", key: "devops-queue", label: "DevOps queue", order: 4 },
    Component: DevOpsQueuePage,
    note: "Stream W2 builds this screen (M-W2c). Email links arrive as /devops?env=…&item=….",
  },
  {
    id: "S24",
    title: "Emergency bypasses",
    path: "/reports/emergency-bypasses",
    tag: "Flagged",
    stream: "W2",
    navFlag: "EmergencyOtp",
    hideForPartner: true,
    navGate: "emergencyReport",
    nav: { group: "Operate", key: "emergency-bypasses", label: "Emergency bypasses", order: 7 },
    Component: EmergencyReportPage,
    note: "Batch 17 Emergency OTP report (design §9, stream E7): every emergency bypass with its moves and skipped clauses, and the requests that never became one; filters, CSV. Chief Pilots see their projects, System Admins all. Behind Features__EmergencyOtp.",
  },
  {
    id: "S9",
    title: "Workflows",
    path: "/p/:projectKey/settings/workflows",
    tag: "MVP-reduced",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    nav: { group: "Settings", key: "workflows", label: "Workflows", order: 1 },
    Component: WorkflowsPage,
    note: "SA-07: the workflow each enabled work type of the project runs (S1); a System Admin (only, since the Settings ruling of 2 Oct 2026) edits who, rules and on/off in a draft and publishes it (S2). Structure editing and rollback are S3 and S4.",
  },
  {
    id: "S9",
    title: "Workflow viewer",
    path: "/p/:projectKey/settings/workflows/:workType",
    tag: "MVP-reduced",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    // Not a nav entry: reached from the Workflows list, one work type at a time.
    Component: WorkflowViewerPage,
  },
  {
    id: "S9",
    title: "Workflow draft",
    path: "/p/:projectKey/settings/workflows/:workType/draft",
    tag: "MVP-reduced",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    // SA-07 slice S2: opened from the list or the viewer; SystemAdmin only (Settings ruling, 2 Oct 2026), as the server.
    Component: WorkflowEditorPage,
  },
  {
    id: "S10",
    title: "User management",
    path: "/admin/users",
    tag: "MVP-critical",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    nav: { group: "Settings", key: "users", label: "Users", order: 3 },
    Component: UsersPage,
  },
  {
    id: "S18",
    title: "Project administration",
    path: "/admin/projects",
    tag: "MVP-critical",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    nav: { group: "Settings", key: "projects", label: "Projects", order: 4 },
    Component: ProjectsAdminPage,
    note: "Product ask (2026-09-18): a page to manage projects (CRUD) and configure member roles. Contract already defined createProject/updateProject/getProjectRoles/replaceProjectRoles; none were implemented anywhere.",
  },
  {
    id: "S18",
    title: "Project members",
    path: "/admin/projects/:projectKey/members",
    tag: "MVP-critical",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    // Not a nav entry: reached from the Projects list's "Members…" link, one project at a time.
    Component: ProjectMembersPage,
  },
  { id: "S11", title: "Log time and worklogs", path: "/items/:ref/time", tag: "Post-go-live", stream: "W1", Component: TimeRedirect },
  { id: "S12", title: "Search results", path: "/search", tag: "MVP-critical", stream: "W1", Component: SearchPage },
  { id: "S13", title: "Estimation breakdown", path: "/p/:projectKey/estimation", tag: "Post-go-live", stream: "W1", note: POST_GO_LIVE },
  {
    id: "S14",
    title: "RAID list",
    path: "/p/:projectKey/raid",
    tag: "MVP-reduced",
    stream: "W1",
    hideForPartner: true,
    navGate: { capabilities: ["menu.raid"] },
    nav: { group: "Register", key: "raid", label: "RAID", order: 1 },
  },
  { id: "S15", title: "My unsynced time", path: "/me/unsynced-time", tag: "Post-go-live", stream: "W1", note: POST_GO_LIVE },
  {
    id: "S16",
    title: "SLA admin",
    path: "/p/:projectKey/settings/sla",
    tag: "MVP-critical",
    stream: "W2",
    hideForPartner: true,
    requiresSystemAdmin: true,
    nav: { group: "Settings", key: "sla-admin", label: "SLA admin", order: 2 },
    Component: SlaAdminPage,
  },
  {
    id: "S19",
    title: "Permissions",
    path: "/admin/permissions",
    tag: "Flagged",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    readOnlyInTestMode: true,
    navFlag: "PermissionsPage",
    nav: { group: "Settings", key: "permissions", label: "Permissions", order: 5 },
    Component: PermissionsPage,
    note: "Settings → Permissions (Revision 79, ruling 2 Oct 2026): the role × permission matrix; capability rows editable by a System Admin with a reason, workflow permissions read-only. Behind Features__PermissionsPage.",
  },
  {
    id: "S20",
    title: "Menu",
    path: "/admin/menu",
    tag: "MVP-reduced",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    nav: { group: "Settings", key: "menu", label: "Menu", order: 6 },
    Component: MenuSettingsPage,
    note: "Settings → Menu (Revision 92, ruling 4 Oct 2026): tick to show a sidebar entry for everyone, arrows to reorder it within its group. Cosmetic only: access follows Settings → Permissions.",
  },
  {
    id: "S21",
    title: "Security",
    path: "/admin/security",
    tag: "Flagged",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    navFlag: "AccessTokens",
    nav: { group: "Settings", key: "security", label: "Security", order: 7 },
    Component: SecurityPage,
    note: "Settings → Security (MCP design phase 1, Revision 95): who may use personal access tokens and MCP, token lifetimes, people with MCP off, everyone's tokens with revoke. Behind Features__AccessTokens.",
  },
  {
    id: "S22",
    title: "Access tokens",
    path: "/me/access-tokens",
    tag: "Flagged",
    stream: "W1",
    // Not a nav entry: the account menu links here (partners included; partners create read-only tokens).
    Component: AccessTokensPage,
    note: "Account → Access tokens (MCP design phase 1, Revision 95): the person's own personal access tokens for MCP clients and scripts. Behind Features__AccessTokens.",
  },
  {
    id: "S23",
    title: "Integration keys",
    path: "/admin/integration-keys",
    tag: "MVP-reduced",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    nav: { group: "Settings", key: "integration-keys", label: "Integration keys", order: 8 },
    Component: IntegrationKeysPage,
    note: "Settings → Integration keys (Revision 97, design MCP-REPORTS-2FA-LOGS §9.4): issue, scope, rotate and revoke integration keys; grant each PMO key its projects and lock it to addresses. A new key is shown once.",
  },
  {
    id: "S28",
    title: "Connected tools",
    path: "/admin/connected-tools",
    tag: "MVP-reduced",
    stream: "W1",
    hideForPartner: true,
    requiresSystemAdmin: true,
    nav: { group: "Settings", key: "connected-tools", label: "Connected tools", order: 9 },
    Component: ConnectedToolsPage,
    note: "Settings → Connected tools (batch 23 S9, request of 18 Oct 2026): read-only report of who has MCP enabled (a live key Settings → Security does not refuse) and who has Telegram linked, with filters, search and an Excel export. Never a key value or a chat id.",
  },
  {
    id: "S-W14",
    title: "Wiki",
    path: "/settings/wiki",
    tag: "Flagged",
    stream: "W1",
    navFlag: "Wiki",
    routeFlag: "Wiki",
    hideForPartner: true,
    requiresSystemAdmin: true,
    nav: { group: "Settings", key: "wiki-settings", label: "Wiki", order: 10 },
    Component: CompanyWikiMembers,
    note: "Wiki W1 S-W14 Settings → Wiki: the Company wiki's editors and managers (D3). The Orphaned pages tab arrives with W4.",
  },
];

const GROUP_ORDER: readonly NavGroupName[] = ["Home", "Plan", "Operate", "Register", "Settings"];

export interface NavAudience {
  isPartner: boolean;
  /** `Session.canOpenSettings`: a SystemAdmin, not in test mode. */
  canOpenSettings: boolean;
  flag(name: string): boolean;
  /** `Session.can` on the project the nav links to (false when there is none). */
  can(code: string): boolean;
  /** `mayOpenDevOpsQueue`. */
  mayOpenDevOpsQueue: boolean;
  /** Batch 17: `mayOpenEmergencyReport` (a Chief Pilot somewhere or a System Admin, not in test mode). Absent = no. */
  mayOpenEmergencyReport?: boolean;
  /** Batch 20: `mayOpenApprovals` (an approval code on some project). Absent = no. */
  mayOpenApprovals?: boolean;
  /** Batch 26: `mayOpenStatusReports` (status.view on some project). Absent = no. */
  mayOpenStatusReports?: boolean;
}

export function isScreenVisible(screen: ScreenDef, audience: NavAudience): boolean {
  if ((screen.hideForPartner || screen.hideNavForPartner) && audience.isPartner) return false;
  if (screen.requiresSystemAdmin && !audience.canOpenSettings) return false;
  if (screen.navFlag && !audience.flag(screen.navFlag)) return false;
  if (screen.navGate === "devopsQueue" && !audience.mayOpenDevOpsQueue) return false;
  if (screen.navGate === "emergencyReport" && audience.mayOpenEmergencyReport !== true) return false;
  if (screen.navGate === "approvals" && audience.mayOpenApprovals !== true) return false;
  if (screen.navGate === "statusReports" && audience.mayOpenStatusReports !== true) return false;
  if (typeof screen.navGate === "object" && !screen.navGate.capabilities.every((code) => audience.can(code))) return false;
  return true;
}

/** `Me.menu` (contract 0.10.50, Settings → Menu): which entries show, system-wide, and their place within their group. */
export interface MenuLayout {
  entries: readonly { key: string; hidden: boolean; position: number }[];
}

/**
 * Settings → Menu locks (server `MenuCatalog`, M1 and M2): Home and every Settings entry always show, whatever a menu says,
 * so no one lands on an empty sidebar and a SystemAdmin can never hide the way back to Settings.
 */
export function isMenuLocked(screen: ScreenDef): boolean {
  return screen.nav?.key === "home" || screen.nav?.group === "Settings";
}

/**
 * The side nav (and the phone nav, the same element restyled): the entries this audience may open, minus those Settings →
 * Menu hides, in the menu's order within each group (the registry's `order` when the server sent no menu or does not know
 * the entry). Hiding is cosmetic only: a hidden page still opens from a direct link, and an entry the audience may not open
 * stays out whatever the menu says.
 */
export function navGroupsFor(audience: NavAudience, menu?: MenuLayout) {
  const placed = new Map((menu?.entries ?? []).map((entry) => [entry.key, entry]));
  const place = (screen: ScreenDef) => placed.get(screen.nav?.key ?? "")?.position ?? screen.nav?.order ?? 0;
  return GROUP_ORDER.map((group) => ({
    label: group,
    items: screens
      .filter((screen) => screen.nav?.group === group && isScreenVisible(screen, audience))
      .filter((screen) => isMenuLocked(screen) || placed.get(screen.nav?.key ?? "")?.hidden !== true)
      .sort((a, b) => place(a) - place(b) || (a.nav?.order ?? 0) - (b.nav?.order ?? 0)),
  })).filter((group) => group.items.length > 0);
}

/** Concrete link for a nav entry. Item routes (:ref) are not in the nav. */
export function screenHref(screen: ScreenDef, projectKey: string): string {
  return screen.path.replace(":projectKey", encodeURIComponent(projectKey));
}
